import { getRouteDistance } from '@features/map/utils/distanceUtils.js';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import { ENDPOINTS } from './constants.js';
import packageSerializer from './serializer.js';

// Get user store reference
function getUserId() {
  const userStore = getStore('user');
  console.log(
    '🚀 ~ getUserId ~ userStore?.state?.current:',
    userStore?.state?.current
  );
  if (userStore?.state?.current?.id) {
    return userStore.state.current.id;
  }
}

// Load packages
bus.on(constants.EVENTS.PACKAGES.LOAD, async (status = '') => {
  const store = getStore('packages');
  store.state.loading = true;
  store.state.error = null;
  store.state.activeStatus = status;

  try {
    const userId = getUserId();
    // Build URL : base already contains ?group=ultraxeno
    let url = `${ENDPOINTS.PACKAGES.LOAD}&userId=${userId}&limit=100`;
    if (status) url += `&status=${status}`;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: 'load-packages',
      url,
      method: 'GET'
    });

    bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
      if (res.id === 'load-packages') {
        store.state.loading = false;
        const isOk = res.success || res.data?.statusCode === 200;
        if (isOk) {
          store.state.packages = packageSerializer.deserializeList(res.data);
          bus.emit(constants.EVENTS.PACKAGES.LOADED);
        } else {
          const error = packageSerializer.deserializeError(res.data);
          store.state.error = error.message || 'Erreur lors du chargement';
          bus.emit(constants.EVENTS.PACKAGES.LOAD_ERROR, store.state.error);
        }
      }
    });
  } catch (error) {
    store.state.loading = false;
    store.state.error = error.message;
    bus.emit(constants.EVENTS.PACKAGES.LOAD_ERROR, error.message);
  }
});

// Calculate delivery fees (server-side)
bus.on(constants.EVENTS.PACKAGES.FEES_CALCULATE, async (data) => {
  const requestId = `packages-fees-${Date.now()}`;

  console.log('[PackageController] Calculating delivery fees:', data);

  try {
    bus.emit(constants.EVENTS.PACKAGES.FEES_CALCULATING);

    // Step 1: Compute real route distance via Google Maps (same as cart controller)

    const srcDstLocation = data.srcDstLocation || {};
    const srcCoords = srcDstLocation.srcPosition?.coordinates;
    const dstCoords = srcDstLocation.dstPosition?.coordinates;

    let distanceKm = data.distanceKm || 0;
    let durationMin = data.durationMin || 0;

    if (
      !distanceKm &&
      srcCoords?.lat &&
      srcCoords?.lng &&
      dstCoords?.lat &&
      dstCoords?.lng
    ) {
      const routeResult = await getRouteDistance(
        { lat: srcCoords.lat, lng: srcCoords.lng },
        { lat: dstCoords.lat, lng: dstCoords.lng },
        (srcDstLocation.checkpoints || []).map((cp) => ({
          lat: cp.coordinates?.lat,
          lng: cp.coordinates?.lng
        }))
      );
      distanceKm = routeResult.distanceKm;
      durationMin = routeResult.durationMin;
      console.log('[PackageController] Route calculated:', routeResult);
    } else if (!distanceKm) {
      console.warn(
        '[PackageController] Missing coordinates, skipping route distance calculation'
      );
    }

    // Step 2: Serialize data for API — inject the calculated distanceKm
    const payload = packageSerializer.serializeFeesCalculation({
      ...data,
      distanceKm,
      durationMin,
      userId: getUserId()
    });

    console.log('[PackageController] Fees calculation payload:', payload);

    // Step 3: Emit network request
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: ENDPOINTS.PACKAGES.FEES_CALCULATE,
      method: 'POST',
      data: payload
    });

    // Handle response
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        const isOk = res.success || res.data?.statusCode === 200;
        if (isOk) {
          const feesData = packageSerializer.deserializeFeesCalculation(res);
          // Inject the real route metadata if not present or 0
          feesData.totalDistance = feesData.totalDistance || distanceKm;
          feesData.totalDuration = feesData.totalDuration || durationMin;
          feesData.distanceFormatted =
            feesData.distanceFormatted || `${distanceKm.toFixed(1)} km`;
          feesData.durationFormatted =
            feesData.durationFormatted || `${Math.round(durationMin)} min`;
          console.log('[PackageController] Fees calculated:', feesData);

          bus.emit(constants.EVENTS.PACKAGES.FEES_CALCULATED, feesData);
        } else {
          const error = packageSerializer.deserializeError(res);
          console.error('[PackageController] Fees calculation error:', error);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur de calcul',
            body:
              error.message || 'Impossible de calculer les frais de livraison'
          });

          bus.emit(constants.EVENTS.PACKAGES.FEES_CALCULATE_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[PackageController] Error calculating fees:', error);
    bus.emit(constants.EVENTS.PACKAGES.FEES_CALCULATE_ERROR, {
      message: error.message,
      code: 'FEES_CALC_ERROR'
    });
  }
});

// Create package delivery from wizard
bus.on(constants.EVENTS.PACKAGES.DELIVERY_CREATE, async (data) => {
  const requestId = `packages-delivery-create-${Date.now()}`;

  console.log('[PackageController] Creating package delivery:', data);

  try {
    bus.emit(constants.EVENTS.PACKAGES.DELIVERY_CREATING);
    bus.emit(constants.EVENTS.LOADING.START);

    // Serialize data for API
    const payload = packageSerializer.serializeDeliveryCreation({
      ...data,
      userId: getUserId()
    });

    console.log('[PackageController] Delivery creation payload:', payload);

    // Emit network request
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: ENDPOINTS.PACKAGES.DELIVERY_CREATE,
      method: 'POST',
      data: payload
    });

    // Handle response
    const responseHandler = async (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        setTimeout(() => {
          bus.emit(constants.EVENTS.LOADING.FINISH);
        }, 300);

        if (res.success) {
          const deliveryData =
            packageSerializer.deserializeDeliveryCreation(res);
          console.log('[PackageController] Delivery created:', deliveryData);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Livraison créée',
            body: 'Votre demande de livraison a été enregistrée avec succès!'
          });

          bus.emit(constants.EVENTS.PACKAGES.DELIVERY_CREATED, deliveryData);
          bus.emit(constants.EVENTS.PACKAGES.LOAD);

          console.log('🚀 ~ responseHandler ~ data:', data);
          console.log('🚀 ~ responseHandler ~ deliveryData:', deliveryData);
          if (!data.paymentLinkEnabled) {
            // Determine payment method similar to order flow
            const rawPaymentMethod = data.paymentMethod;
            const normalizedMethod = rawPaymentMethod
              ?.toLowerCase()
              .replace(/[-_]/g, '');

            const userStore = getStore('user');
            const user = userStore?.state?.current || {};
            const phoneNumber = data.paymentPhone;
            const firstName =
              user.firstName || user.name?.split(' ')[0] || 'Client';
            const lastName =
              user.lastName ||
              user.name?.split(' ').slice(1).join(' ') ||
              'Rapidoo';

            const packageId = deliveryData.id;
            const amount = deliveryData.pricing?.total ?? 0;

            console.log(
              '[PackageController] Payment — packageId:',
              packageId,
              'amount:',
              amount,
              'method:',
              rawPaymentMethod,
              '→',
              normalizedMethod
            );

            if (normalizedMethod === 'mobilemoney') {
              const txId = `${packageId}-mm-${Date.now()}`;
              const txRequestId = `REQ-${packageId}-${Date.now()}`;
              console.log(
                '[PackageController] Initiating mobile money payment, phone:',
                phoneNumber
              );
              const provider = await getProviderToUse(true);
              console.log('[PackageController] Provider:', provider);
              if (!provider) {
                bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
                  type: 'error',
                  title: 'Erreur de paiement',
                  body: 'Impossible de déterminer le fournisseur de paiement'
                });
                return;
              }

              bus.emit(constants.EVENTS.NETWORK.REQUEST, {
                id: `payment-mm-${packageId}`,
                url: 'https://api.monrapidoo.com/payment/collect/mobileMoney?group=ultraxeno',
                method: 'POST',
                data: {
                  id: txId,
                  requestId: txRequestId,
                  firstName: firstName,
                  lastName: lastName,
                  service: 'rapidoo',
                  amount: amount,
                  provider,
                  countryCode: 'BJ',
                  currency: 'XOF',
                  phoneNumber: phoneNumber,
                  narration: 'package payment',
                  metadata: {
                    provider,
                    id: txId,
                    requestId: txRequestId,
                    accountNumber: phoneNumber,
                    eventTopic: 'rapidoo-transactions',
                    eventToEmit: 'packagePaymentCollected',
                    package: deliveryData
                  }
                }
              });
            } else if (normalizedMethod === 'wallet') {
              const txId = `${packageId}-wallet-${Date.now()}`;
              const txRequestId = `REQ-${packageId}-${Date.now()}`;
              console.log(
                '[PackageController] Initiating wallet debit for package:',
                packageId
              );
              bus.emit(constants.EVENTS.NETWORK.REQUEST, {
                id: `payment-wallet-${packageId}`,
                url: 'https://api.monrapidoo.com/wallet/debit?group=ultraxeno',
                method: 'POST',
                data: {
                  id: txId,
                  userId: `wallet_${getUserId()}`,
                  service: 'rapidoo',
                  country: 'ANY',
                  processor: 'rapidoWallet',
                  currency: 'XOF',
                  amount: amount,
                  reqId: txRequestId,
                  metadata: {
                    accountNumber: phoneNumber,
                    eventTopic: 'rapidoo-transactions',
                    eventToEmit: 'packageWalletWithdrawn',
                    package: deliveryData
                  }
                }
              });
            }
          }
        } else {
          const error = packageSerializer.deserializeError(res);
          console.error('[PackageController] Delivery creation error:', error);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur',
            body: error.message || 'Impossible de créer la livraison'
          });

          bus.emit(constants.EVENTS.PACKAGES.DELIVERY_CREATE_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[PackageController] Error creating delivery:', error);
    setTimeout(() => {
      bus.emit(constants.EVENTS.LOADING.FINISH);
    }, 300);

    bus.emit(constants.EVENTS.PACKAGES.DELIVERY_CREATE_ERROR, {
      message: error.message,
      code: 'DELIVERY_CREATE_ERROR'
    });
  }
});

bus.on(constants.EVENTS.PACKAGES.LAUNCH_PAYMENT_LINK_PAYMENT, async (event) => {
  const { paymentData, packageId, packageData } = event || {};
  if (!packageId || !paymentData) {
    console.warn(
      '[PackageController] Missing packageId or paymentData for payment launch'
    );
    return;
  }

  const {
    phoneNumber,
    method = paymentData.paymentMethod || paymentData.method
  } = paymentData;
  const amount =
    packageData?.pricing?.total ?? packageData?.pricing?.deliveryFee ?? 0;

  const userStore = getStore('user');
  const user = userStore?.state?.current || {};
  const firstName = user.firstName || user.name?.split(' ')[0] || 'Client';
  const lastName =
    user.lastName || user.name?.split(' ').slice(1).join(' ') || '';

  const txId = `${packageId}-${method}-${Date.now()}`;
  const txRequestId = `REQ-${packageId}-${Date.now()}`;

  const provider = await getProviderToUse(true);
  console.log('[PackageController] Provider:', provider);

  if (!provider) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de paiement',
      body: 'Impossible de déterminer le fournisseur de paiement'
    });
    return;
  }

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: `payment-mm-${packageId}`,
    url: 'https://api.monrapidoo.com/payment/collect/mobileMoney?group=ultraxeno',
    method: 'POST',
    data: {
      id: txId,
      requestId: txRequestId,
      firstName,
      lastName,
      service: 'rapidoo',
      amount,
      provider,
      countryCode: 'BJ',
      currency: 'XOF',
      phoneNumber,
      narration: 'package payment',
      metadata: {
        provider,
        id: txId,
        requestId: txRequestId,
        accountNumber: phoneNumber,
        eventTopic: 'rapidoo-transactions',
        eventToEmit: 'packagePaymentCollected',
        package: packageData
      }
    }
  });
});

// Create simple package (old method)
bus.on(constants.EVENTS.PACKAGES.CREATE, async (packageData) => {
  const userId = getUserId();

  // Require authentication for package creation
  if (!userId) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Authentication requise',
      body: 'Vous devez être connecté pour créer un colis',
      type: 'error'
    });
    return;
  }

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: 'create-package',
    url: ENDPOINTS.PACKAGES.CREATE,
    method: 'POST',
    data: {
      ...packageData,
      userId
    }
  });

  bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === 'create-package') {
      if (res.success) {
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Colis créé',
          body: 'Votre demande de livraison a été enregistrée'
        });
        bus.emit(constants.EVENTS.PACKAGES.LOAD);
      } else {
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Erreur',
          body: res.data?.message || 'Impossible de créer le colis'
        });
      }
    }
  });
});

// Search for a package by tracking code
bus.on(constants.EVENTS.PACKAGES.TRACKING_SEARCH, async (trackingCode) => {
  const requestId = `tracking-search-${Date.now()}`;

  console.log('[PackageController] Searching for package:', trackingCode);

  if (
    !trackingCode ||
    typeof trackingCode !== 'string' ||
    trackingCode.trim().length === 0
  ) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Code invalide',
      body: 'Veuillez entrer un numéro de tracking valide'
    });
    bus.emit(constants.EVENTS.PACKAGES.TRACKING_SEARCH_ERROR, {
      message: 'Code de tracking invalide'
    });
    return;
  }

  try {
    bus.emit(constants.EVENTS.PACKAGES.TRACKING_SEARCHING);

    // Build URL with tracking reference
    const url = ENDPOINTS.PACKAGES.TRACKING_SEARCH.replace(
      ':code',
      trackingCode.trim()
    );

    console.log('[PackageController] Tracking search URL:', url);

    // Emit network request
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET'
    });

    // Handle response
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        if (res.success) {
          const packageData = res.data;
          console.log('[PackageController] Package found:', packageData);

          bus.emit(constants.EVENTS.PACKAGES.TRACKING_FOUND, packageData);
        } else {
          console.error('[PackageController] Package not found:', res);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Colis non trouvé',
            body: 'Aucun colis trouvé avec ce numéro de tracking'
          });

          bus.emit(constants.EVENTS.PACKAGES.TRACKING_NOT_FOUND, {
            message: 'Colis non trouvé',
            code: trackingCode
          });
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[PackageController] Error searching for package:', error);

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de recherche',
      body: 'Impossible de rechercher le colis. Veuillez réessayer.'
    });

    bus.emit(constants.EVENTS.PACKAGES.TRACKING_SEARCH_ERROR, {
      message: error.message,
      code: 'TRACKING_SEARCH_ERROR'
    });
  }
});

// Load single package detail
bus.on(constants.EVENTS.PACKAGES.DETAIL, async (packageId) => {
  if (!packageId) return;
  const requestId = `package-detail-${packageId}`;

  try {
    bus.emit(constants.EVENTS.PACKAGES.DETAIL_LOADING);

    const url = `${ENDPOINTS.PACKAGES.DETAILS}`
      .replace(':id', packageId)
      .replace('{packageId}', packageId)
      .replace('{id}', packageId);

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET'
    });

    const responseHandler = (res) => {
      if (res.id !== requestId) return;
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      const isOk = res.success || res.data?.statusCode === 200;
      if (isOk) {
        const raw = res.data?.data || res.data || res;
        const pkg = packageSerializer.deserializePackage(raw);
        bus.emit(constants.EVENTS.PACKAGES.DETAIL_LOADED, pkg);
      } else {
        const error = packageSerializer.deserializeError(res.data || res);
        bus.emit(
          constants.EVENTS.PACKAGES.DETAIL_ERROR,
          error.message || 'Erreur lors du chargement du colis'
        );
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    bus.emit(constants.EVENTS.PACKAGES.DETAIL_ERROR, error.message);
  }
});

async function getProviderToUse(isCollect) {
  try {
    const feeUrl =
      'https://api.monrapidoo.com/settings/byKey?group=ultraxeno&key=fees&service=rapidoo';
    const res = await fetch(feeUrl);

    if (!res.ok) {
      return null;
    }

    const responseData = await res.json();

    // Vérification du status code API et de la présence des paramètres requis
    if (
      responseData?.statusCode !== 200 ||
      !responseData?.data?.paymentProviders
    ) {
      return null;
    }

    const collectionProviders =
      responseData.data.paymentProviders.collectionProviders;
    const withdrawalProviders =
      responseData.data.paymentProviders.withdrawalProviders;

    if (isCollect) {
      return collectionProviders;
    } else {
      return withdrawalProviders;
    }
  } catch (error) {
    console.error(
      'Erreur lors de la récupération du fournisseur de paiement :',
      error
    );
    return null;
  }
}

export default {
  getUserId
};
